// The camera's half of playback speed: its lead over the board, and the
// speed ladder the player sets.
//
// The crowd ramp — how much faster an opponent's turn plays — is the rules'
// (`src/engine/api/tempo.ts`, since day 2 of the client split) and arrives as
// `BoardFacts.tempo`. What is here multiplies onto it.

/**
 * How much quicker the camera is than the board it is following.
 *
 * **On top of the tempo, not instead of it.** The pan is what a turn is spent
 * waiting on once everything else has been sped up: the move, the factory and
 * the guns all finish in a third of the time against a crowd, and the camera
 * still has the same distance to cross. A flat extra third is a small number
 * that lands where the remaining wait actually is.
 */
export const CAMERA_LEAD = 1.3;

/**
 * The factor the camera's lead is divided by — playback speed and the camera's
 * own head start together.
 *
 * One value because the two must agree. The renderer scales its whole frame by
 * the tempo, camera included, so a lead measured against unscaled time would
 * leave the driver waiting on a pan that landed long ago — which is exactly
 * how this was reported: everything got faster and the turn did not.
 */
export const leadFor = (tempo: number): number => tempo * CAMERA_LEAD;

/**
 * The animation speeds a player may ask for, as multipliers over everything
 * above.
 *
 * **A scale the player sets, not a rule the board applies.** Everything above
 * this line is the game deciding how fast to play a turn back — the crowd ramp,
 * the camera's lead. This is somebody saying how fast they like watching, and
 * the two multiply: a busy board still plays its opponents quicker than the
 * player's own turn, and the whole thing runs at whatever the player set.
 *
 * **So it scales the player's own turn too**, which the crowd ramp deliberately
 * never does. That rule is about a speed-up nobody asked for — taking away the
 * animation the player is trying to read. Asking for it is the other thing.
 *
 * **Steps rather than a free number**, and finer than the transports' `1/2/4`
 * because that is what the ask was: the difference between 0.75x and 1.5x is
 * one a person can feel, and the ladder is what lets the control show the speed
 * that is actually playing. `snapSpeed` is why a stored value can never sit
 * between two stops.
 *
 * **It runs below 1**, which nothing else here does. Every existing speed
 * control makes a game somebody is not playing go faster; this is also the one
 * place to slow a board down and watch a tick resolve, which is a real thing to
 * want on a board where a move, a factory and a lane all happen at once.
 */
export const SPEEDS = [0.25, 0.5, 0.75, 1, 1.5, 2, 3, 4, 6, 8] as const;

/** Ordinary speed: the board as it has always played. */
export const DEFAULT_SPEED = 1;

/**
 * The nearest speed on the ladder, and the default for anything that is not a
 * number at all.
 *
 * The value comes off `localStorage`, which is a store a person is allowed to
 * edit and which holds whatever shape shipped the day they last played — so
 * `undefined`, a string and `0` all have to arrive as something the two clocks
 * can be divided by. A zero is the one that matters: the driver paces itself at
 * `STEP_MS / pace`, so an unclamped nought is a turn that never advances.
 *
 * **Snapped rather than clamped**, so the control cannot show one speed while
 * the board plays another.
 */
export function snapSpeed(value: unknown): number {
  if (typeof value !== "number" || !Number.isFinite(value)) return DEFAULT_SPEED;
  let best: number = SPEEDS[0];
  for (const step of SPEEDS) {
    if (Math.abs(step - value) < Math.abs(best - value)) best = step;
  }
  return best;
}
